import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearSession,
  fetchCurrentUser,
  getStoredToken,
  getStoredUser,
  loginRequest,
  logoutRequest,
  resolveAssetUrl,
  storeSession,
} from '../api/client';

const AuthContext = createContext(null);

function normalizeUser(profile) {
  if (!profile) return null;
  return {
    id: profile.id,
    username: profile.username,
    displayName: profile.displayName,
    email: profile.email,
    role: profile.role || 'account_user',
    accountId: profile.accountId,
    accountName: profile.accountName,
    logoUrl: profile.logoUrl ? resolveAssetUrl(profile.logoUrl) : null,
    primaryColor: profile.primaryColor,
    onboardingComplete: profile.onboardingComplete !== false,
    mustResetPassword: profile.mustResetPassword === true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => normalizeUser(getStoredUser()));
  const [token, setToken] = useState(() => getStoredToken());
  const [initializing, setInitializing] = useState(Boolean(getStoredToken()));
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function verifySession() {
      const storedToken = getStoredToken();
      if (!storedToken) {
        if (!cancelled) {
          setInitializing(false);
          setUser(null);
          setToken(null);
        }
        return;
      }

      try {
        const profile = await fetchCurrentUser();
        if (cancelled) return;
        const nextUser = normalizeUser(profile);
        setUser(nextUser);
        setToken(storedToken);
        setAuthError(null);
      } catch (error) {
        if (cancelled) return;
        clearSession();
        setUser(null);
        setToken(null);
        setAuthError(error.message || 'Your session has expired. Please sign in again.');
      } finally {
        if (!cancelled) setInitializing(false);
      }
    }

    verifySession();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const result = await loginRequest(username, password);
    const nextUser = normalizeUser(result.user);
    storeSession({ accessToken: result.accessToken, user: nextUser });
    setToken(result.accessToken);
    setUser(nextUser);
    setAuthError(null);
    return nextUser;
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((current) => {
      if (!current) return current;
      const nextUser = { ...current, ...patch };
      const storedToken = getStoredToken();
      if (storedToken) {
        storeSession({ accessToken: storedToken, user: nextUser });
      }
      return nextUser;
    });
  }, []);

  const refreshUser = useCallback(async ({ bustLogo = false } = {}) => {
    const profile = await fetchCurrentUser();
    let nextUser = normalizeUser(profile);
    if (bustLogo && nextUser.logoUrl) {
      const separator = nextUser.logoUrl.includes('?') ? '&' : '?';
      nextUser = { ...nextUser, logoUrl: `${nextUser.logoUrl}${separator}t=${Date.now()}` };
    }
    const storedToken = getStoredToken();
    if (storedToken) {
      storeSession({ accessToken: storedToken, user: nextUser });
    }
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // Always clear local session even if the API call fails.
    } finally {
      clearSession();
      setToken(null);
      setUser(null);
      setAuthError(null);
    }
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';
  const needsOnboarding =
    Boolean(user) && !isSuperAdmin && user.onboardingComplete === false;
  const mustResetPassword = Boolean(user) && !isSuperAdmin && user.mustResetPassword === true;

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isSuperAdmin,
      needsOnboarding,
      mustResetPassword,
      initializing,
      authError,
      setAuthError,
      login,
      logout,
      updateUser,
      refreshUser,
    }),
    [user, token, isSuperAdmin, needsOnboarding, mustResetPassword, initializing, authError, login, logout, updateUser, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
